
import Header from '../components/Header'
import Footer from '../components/Footer'

export default function TroubleshootingManualPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Page Header */}
      <section className="relative bg-gradient-to-br from-primary-700 to-primary-900 text-white py-24 lg:py-32">
        <div className="absolute inset-0 opacity-10">
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                                linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-80 h-80 bg-accent-orange/10 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 backdrop-blur-sm mb-4">
              资料中心
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              故障排查和维修手册
            </h1>
            <p className="text-lg text-white/80 max-w-2xl mx-auto">
              提供常见故障的排查方法和维修指导，帮助您快速解决问题
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Table of Contents */}
          <div className="bg-secondary-50 rounded-2xl p-6 mb-12">
            <h2 className="text-2xl font-bold text-secondary-900 mb-4">目录</h2>
            <ul className="space-y-2">
              <li><a href="#overview" className="text-primary-600 hover:text-primary-700 transition-colors">1. 产品概述</a></li>
              <li><a href="#safety" className="text-primary-600 hover:text-primary-700 transition-colors">2. 安全注意事项</a></li>
              <li><a href="#troubleshooting" className="text-primary-600 hover:text-primary-700 transition-colors">3. 故障排查</a></li>
              <li><a href="#maintenance" className="text-primary-600 hover:text-primary-700 transition-colors">4. 日常维护</a></li>
              <li><a href="#wiring" className="text-primary-600 hover:text-primary-700 transition-colors">5. 电气接线图</a></li>
            </ul>
          </div>

          {/* Overview Section */}
          <section id="overview" className="mb-16">
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">1. 产品概述</h2>
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <p className="text-secondary-600 mb-4">
                  本产品是一种高效节能的生物质颗粒炉，采用先进的燃烧技术，热效率达到92%以上，适用于工业和商业供暖。
                </p>
                <p className="text-secondary-600 mb-4">
                  产品主要由燃烧系统、进料系统、控制系统和热交换系统组成，具有智能控制、安全可靠、环保节能等特点。
                </p>
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img 
                  src="https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=biomass%20granule%20furnace%20product%20overview&image_size=landscape_4_3" 
                  alt="产品概述" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </section>

          {/* Safety Section */}
          <section id="safety" className="mb-16">
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">2. 安全注意事项</h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
              <h3 className="text-lg font-semibold text-red-700 mb-2">警告</h3>
              <ul className="list-disc list-inside space-y-2 text-secondary-700">
                <li>在进行任何维修操作前，必须切断电源并等待设备冷却</li>
                <li>请勿在设备运行时进行维修操作</li>
                <li>维修时应使用正确的工具和防护装备</li>
                <li>对于复杂的故障，应联系专业技术人员进行维修</li>
              </ul>
            </div>
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-4">
              <h3 className="text-lg font-semibold text-yellow-700 mb-2">注意</h3>
              <ul className="list-disc list-inside space-y-2 text-secondary-700">
                <li>定期检查设备的运行状态</li>
                <li>按照说明书的要求进行日常维护</li>
                <li>使用符合要求的生物质颗粒燃料</li>
                <li>保持设备周围通风良好</li>
              </ul>
            </div>
          </section>

          {/* Troubleshooting Section */}
          <section id="troubleshooting" className="mb-16">
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">3. 故障排查</h2>
            
            {/* Error Codes Table */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">故障代码表</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-secondary-200 border border-secondary-200 rounded-lg">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">故障代码</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">故障描述</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">可能原因</th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">解决方法</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-secondary-200">
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">E01</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">点火失败</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">燃料不足或质量差</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">添加燃料或更换燃料</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">E02</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">温度传感器故障</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">传感器损坏或连接松动</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">检查连接或更换传感器</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">E03</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">进料电机故障</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">电机损坏或卡涩</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">检查电机或联系维修</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">E04</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">风机故障</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">风机损坏或电源问题</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">检查风机或电源</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900">E05</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">过热保护</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">温度过高</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-600">检查通风或减少负荷</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Troubleshooting Flowchart */}
            <div className="mb-8">
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">故障排查流程图</h3>
              <div className="bg-white border border-secondary-200 rounded-lg p-4 shadow-sm">
                <img 
                  src="https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=troubleshooting%20flowchart%20for%20biomass%20furnace&image_size=landscape_4_3" 
                  alt="故障排查流程图" 
                  className="w-full h-auto"
                />
              </div>
            </div>

            {/* Common Issues */}
            <div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-4">常见故障及解决方法</h3>
              <div className="space-y-6">
                <div className="bg-white border border-secondary-200 rounded-lg p-6 shadow-sm">
                  <h4 className="text-lg font-medium text-secondary-900 mb-2">1. 设备无法启动</h4>
                  <ul className="list-disc list-inside space-y-2 text-secondary-600">
                    <li>检查电源是否连接</li>
                    <li>检查熔断器是否熔断</li>
                    <li>检查控制板是否正常工作</li>
                  </ul>
                </div>
                <div className="bg-white border border-secondary-200 rounded-lg p-6 shadow-sm">
                  <h4 className="text-lg font-medium text-secondary-900 mb-2">2. 燃烧不充分</h4>
                  <ul className="list-disc list-inside space-y-2 text-secondary-600">
                    <li>检查燃料质量</li>
                    <li>检查通风是否良好</li>
                    <li>清理燃烧室和烟道</li>
                  </ul>
                </div>
                <div className="bg-white border border-secondary-200 rounded-lg p-6 shadow-sm">
                  <h4 className="text-lg font-medium text-secondary-900 mb-2">3. 温度不稳定</h4>
                  <ul className="list-disc list-inside space-y-2 text-secondary-600">
                    <li>检查温度传感器</li>
                    <li>检查控制系统</li>
                    <li>检查燃料供应是否稳定</li>
                  </ul>
                </div>
                <div className="bg-white border border-secondary-200 rounded-lg p-6 shadow-sm">
                  <h4 className="text-lg font-medium text-secondary-900 mb-2">4. 噪音过大</h4>
                  <ul className="list-disc list-inside space-y-2 text-secondary-600">
                    <li>检查风机是否正常</li>
                    <li>检查进料系统是否卡涩</li>
                    <li>检查设备是否安装牢固</li>
                  </ul>
                </div>
              </div>
            </div>
          </section>

          {/* Maintenance Section */}
          <section id="maintenance" className="mb-16">
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">4. 日常维护</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white border border-secondary-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">定期维护</h3>
                <ul className="space-y-4">
                  <li>
                    <h4 className="font-medium text-secondary-900">每日维护</h4>
                    <ul className="list-disc list-inside space-y-1 text-secondary-600 ml-4">
                      <li>检查燃料余量</li>
                      <li>清理灰盒</li>
                      <li>检查设备运行状态</li>
                    </ul>
                  </li>
                  <li>
                    <h4 className="font-medium text-secondary-900">每周维护</h4>
                    <ul className="list-disc list-inside space-y-1 text-secondary-600 ml-4">
                      <li>清理燃烧室</li>
                      <li>检查风机和电机</li>
                      <li>检查密封件</li>
                    </ul>
                  </li>
                  <li>
                    <h4 className="font-medium text-secondary-900">每月维护</h4>
                    <ul className="list-disc list-inside space-y-1 text-secondary-600 ml-4">
                      <li>清理烟道</li>
                      <li>检查传感器</li>
                      <li>检查电气连接</li>
                    </ul>
                  </li>
                </ul>
              </div>
              <div className="bg-white border border-secondary-200 rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold text-secondary-900 mb-4">维护注意事项</h3>
                <ul className="list-disc list-inside space-y-2 text-secondary-600">
                  <li>使用专用工具进行维护</li>
                  <li>维护前必须切断电源</li>
                  <li>维护时应佩戴防护装备</li>
                  <li>按照说明书的要求进行维护</li>
                  <li>记录维护情况，建立维护档案</li>
                  <li>对于复杂的维护工作，应联系专业技术人员</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Wiring Diagram Section */}
          <section id="wiring">
            <h2 className="text-2xl font-bold text-secondary-900 mb-6">5. 电气接线图</h2>
            <div className="bg-white border border-secondary-200 rounded-lg p-6 shadow-sm">
              <img 
                src="https://trae-api-cn.mchost.guru/api/ide/v1/text_to_image?prompt=electrical%20wiring%20diagram%20for%20biomass%20furnace&image_size=landscape_4_3" 
                alt="电气接线图" 
                className="w-full h-auto"
              />
              <p className="text-secondary-600 mt-4">
                注：电气接线图仅供参考，具体接线应根据实际设备型号和配置进行。如有疑问，请联系专业电气技术人员。
              </p>
            </div>
          </section>
        </div>
      </section>
      
      <Footer />
    </div>
  )
}
